import React, { useState, useContext, useEffect } from "react";
import { Container, Row } from "reactstrap";
import { useParams, useNavigate } from "react-router-dom";
import { usePostStudyAssessmentHook } from "../api/usePostStudyAssessmentHook";
import { formatDate } from "common/service/CommonService";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import styles from "../../AmbulatoryVisit/css/ambulatoryVisit.module.scss";
import { BreadcrumbContext } from "Layouts/Header";
import FullPageLoader from "Components/Common/FullPageLoader";
import SectionCard from "Components/Common/SectionCard";
import DetailField from "Components/Common/DetailField";
import Commonbadge from "Components/Common/Commonbadge";
import CommonButton from "Components/Common/CommonButton";

// ─── Tab Key Type ────────────────────────────────────────────────────────────
type AssessmentViewTab = "vital_signs" | "laboratory";

const TABS: { key: AssessmentViewTab; label: string; icon: string }[] = [
  { key: "vital_signs", label: "Vital Signs Measurement", icon: "ri-pulse-line" },
  { key: "laboratory", label: "Laboratory Assessment", icon: "ri-flask-line" },
];

const ViewPostStudyAssessmentSubjectDetail: React.FC = () => {
  document.title = "View Post Study Assessment | ZENTIXS VISTA";

  const { subjectId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AssessmentViewTab>("vital_signs");

  const studyid = Number(sessionStorage.getItem("activestudyid") || 1);

  const subjectno = subjectId || "";
  const { SelectAssessmentQuery } = usePostStudyAssessmentHook({ studyid, subjectno });
  const { data: assessmentDataResponse, isLoading, isFetching } = SelectAssessmentQuery;
  const data = assessmentDataResponse?.data;

  const breadcrumbContext = useContext(BreadcrumbContext);

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Post Study Assessment", link: "/study/detail/poststudyassessment" },
      { text: `View Subject ${subjectId || ""} Assessment` },
    ]);

    return () => {
      breadcrumbContext?.setBreadcrumbItems?.([]);
    };
  }, [subjectId]);


  if (isLoading || isFetching) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader message="Fetching assessment details..." />
      </Container>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="fw-700 mb-1">View Post Study Assessment</h3>
            <p className="text-muted mb-0">
              Study Code: {data?.studycode || "-"} | Subject No: {subjectId}
            </p>
          </div>
          <div>
          </div>
        </div>

        {/* ── Tab Switcher ─────────────────────────────────────────────── */}
        <div className="mb-4">
          <div className="p-1 bg-light rounded-pill d-inline-flex border">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn btn-sm px-3 rounded-pill fw-semibold border-0 d-flex align-items-center gap-1 ${activeTab === tab.key ? "text-primary shadow-sm" : "text-muted"
                  }`}
                style={{
                  fontSize: "12px",
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  backgroundColor:
                    activeTab === tab.key ? "#ffffff" : "transparent",
                  transition: "background-color 0.15s ease",
                }}
                onClick={() => setActiveTab(tab.key)}
              >
                <i className={tab.icon} style={{ fontSize: "13px" }} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Tab Content ──────────────────────────────────────────────── */}
        <div className="d-flex flex-column gap-3">

          {!data && (
            <div className="d-flex flex-column align-items-center justify-content-center text-center mt-5 pt-5" style={{ minHeight: "40vh" }}>
              <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
              <h4 className="fw-600">Assessment Not Found</h4>
              <p className="text-muted">
                The assessment record you are looking for does not exist or has not been completed yet.
              </p>
            </div>
          )}

          {data && (
            <>
              {/* Subject Identification Header Card */}
              <SectionCard
                icon="ri-user-search-line"
                title="Subject Identification"
                subtitle="Details of the subject and post study assessment status."
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 w-100">
                  <div className="d-flex align-items-center gap-3 w-100">
                    <div className={`${styles.detailMetaGrid} flex-grow-1`}>
                      <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                        <i className="ri-user-3-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                        <div>
                          <div className={styles.metaLabel}>Subject No.</div>
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                              {subjectId || "-"}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                        <i className="ri-calendar-line fs-20 text-muted" style={{ marginRight: "4px" }} />
                        <div>
                          <div className={styles.metaLabel}>Assessment Date</div>
                          <div className={styles.metaValue} style={{ color: "#0f172a", fontSize: "14px", fontWeight: 700 }}>
                            {data.assessmentdate ? formatDate(data.assessmentdate) : "-"}
                          </div>
                        </div>
                      </div>

                      <div className={`${styles.metaBlock} d-flex align-items-center gap-2`}>
                        <i className="ri-checkbox-circle-fill fs-20 text-muted" style={{ marginRight: "4px" }} />
                        <div>
                          <div className={styles.metaLabel}>Subject Status</div>
                          <Commonbadge status={data.participantstatus || "completed"} style={{ width: "90px", fontSize: "12px" }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* TAB 1: Vital Signs */}
              {activeTab === "vital_signs" && (
                <SectionCard
                  icon="ri-pulse-line"
                  title="Vital Signs Measurement"
                  subtitle="Physical examination, vital signs, system assessments, and participant status."
                >
                  <Row>
                    <DetailField label="Position" value={data.position} />
                    <DetailField label="Actual Time" value={data.actualtime} />
                    <DetailField label="Blood Pressure (mmHg)" value={data.bloodpressure} />
                    <DetailField label="Pulse Rate (bpm)" value={data.pulserate} />
                    <DetailField label="Respiration Rate (/min)" value={data.respirationrate} />
                    <DetailField label="Oral Temp (°F)" value={data.oraltemperature} />
                    <DetailField label="Well Being Assessment" value={data.wellbeingassessment} />
                    <DetailField label="Respiratory System" value={data.respiratorysystem} />
                    <DetailField label="Gastro Intestinal System" value={data.gastrointestinalsystem} />
                    <DetailField label="Cardio Vascular System" value={data.cardiovascularsystem} />
                    <DetailField label="Central Nervous System" value={data.centralnervoussystem} />
                    <DetailField label="Participant Status" value={data.participantstatus} />
                    <DetailField label="Remarks" value={data.remarks} />
                  </Row>
                </SectionCard>
              )}

              {/* TAB 2: Laboratory Assessment */}
              {activeTab === "laboratory" && (
                <SectionCard
                  icon="ri-flask-line"
                  title="Laboratory Assessment"
                  subtitle="Record blood sample collection details and laboratory assessment findings."
                >
                  <Row>
                    <DetailField label="Blood samples collected" value={data.bloodsamplecollected === true ? "Yes" : data.bloodsamplecollected === false ? "No" : "-"} />
                    <DetailField label="Volume of Blood collected (mL)" value={data.bloodvolume ? `${data.bloodvolume} ML` : "-"} />
                    <DetailField label="Any clinically significant abnormal value" value={data.abnormalvalues === true ? "Yes" : data.abnormalvalues === false ? "No" : "-"} />
                    {data.abnormalvalues === true && (
                      <DetailField label="Abnormal Value Details" value={data.abnormalvaluesdetail || "-"} />
                    )}
                  </Row>
                </SectionCard>
              )}
            </>
          )}

        </div>
      </Container>
    </div>
  );
};

export default ViewPostStudyAssessmentSubjectDetail;
